// Today's warm-ups and last warm-up jump, when they are not the usual ones.
//
// The USUAL ones are a fact about how this lifter warms up, so they live on the
// lift record (`lastJump`, the start) and its step rows, and Kit opens them.
// TODAY'S are a fact about today — a sore shoulder, a heavy week — so they live
// in session storage beside the ramp's position (`work-up:at:<id>`), and
// tomorrow opens on the usual ones without anybody having to put them back.
//
// Reactive, so the Lifts row, the Ramp and the scheme preview move together.
// Every screen asks `jumpFor` and `schemeFor` (or `rampFor`, which asks both);
// none reads storage itself, because two readings of "what goes on the bar" is
// the one defect this app cannot have.
import { reactive } from 'vue';
import { buildRamp, savedJump, clampJump, tidyScheme } from './loading.js';
import { savedScheme, saveScheme } from './seed.js';

const PREFIX = 'work-up:jump:';
const STEPS = 'work-up:steps:';
const today = reactive({});
const todaySteps = reactive({});

// Read once. Storage can be missing or refuse (private mode, tests), and a jump
// that can't be remembered is still a jump for as long as the app is open.
try {
  for (let i = 0; i < sessionStorage.length; i++) {
    const k = sessionStorage.key(i);
    if (k?.startsWith(PREFIX)) {
      const v = Number(sessionStorage.getItem(k));
      if (v > 0) today[k.slice(PREFIX.length)] = v;
    } else if (k?.startsWith(STEPS)) {
      try { todaySteps[k.slice(STEPS.length)] = tidyScheme(JSON.parse(sessionStorage.getItem(k))); } catch { /* not ours */ }
    }
  }
} catch { /* in memory only */ }

/** Today's jump for a lift, or null when there isn't one or it matches the usual. */
export function todayJump(lift, unit) {
  const t = today[String(lift?.id)];
  if (t == null) return null;
  const w = clampJump(t, unit);
  return w === savedJump(lift, unit) ? null : w;
}

/** The jump every screen builds the ramp with: today's, else the lift's, else the default. */
export const jumpFor = (lift, unit) => todayJump(lift, unit) ?? savedJump(lift, unit);

export function clearTodayJump(lift) {
  const id = String(lift.id);
  delete today[id];
  try { sessionStorage.removeItem(PREFIX + id); } catch { /* in memory only */ }
}

/** Landing back on the usual jump clears today's rather than storing a copy of it. */
export function setTodayJump(lift, w, unit) {
  const next = clampJump(w, unit);
  if (next === savedJump(lift, unit)) return clearTodayJump(lift);
  const id = String(lift.id);
  today[id] = next;
  try { sessionStorage.setItem(PREFIX + id, String(next)); } catch { /* in memory only */ }
}

const same = (a, b) => JSON.stringify(tidyScheme(a)) === JSON.stringify(tidyScheme(b));

/** Today's warm-ups for a lift, or null when there aren't any or they match the saved ones. */
export function todayScheme(lift) {
  const t = todaySteps[String(lift?.id)];
  if (!t) return null;
  return same(t, savedScheme(lift)) ? null : t;
}

/** The warm-ups every screen builds the ramp with: today's, else the lift's saved ones. */
export const schemeFor = (lift) => todayScheme(lift) ?? savedScheme(lift);

export function clearTodayScheme(lift) {
  const id = String(lift.id);
  delete todaySteps[id];
  try { sessionStorage.removeItem(STEPS + id); } catch { /* in memory only */ }
}

/** Same rule as the jump: a scheme equal to the saved one clears today's. */
export function setTodayScheme(lift, scheme) {
  if (same(scheme, savedScheme(lift))) return clearTodayScheme(lift);
  const id = String(lift.id);
  const next = tidyScheme(scheme);
  todaySteps[id] = next;
  try { sessionStorage.setItem(STEPS + id, JSON.stringify(next)); } catch { /* in memory only */ }
}

/** Back to the usual warm-ups and jump, for today. */
export function clearToday(lift) {
  clearTodayScheme(lift);
  clearTodayJump(lift);
}

/** Today's warm-ups and jump become the usual ones, and today stops being different. */
export function saveTodayAsDefault(lift, unit) {
  const jump = jumpFor(lift, unit);
  // Written onto the lift only when it moves, as Kit does, so a lift nobody has
  // touched keeps reading the default for whichever unit the kit is in.
  saveScheme(lift, schemeFor(lift), jump === savedJump(lift, unit) ? {} : { lastJump: jump });
  clearToday(lift);
}

/**
 * THE ramp for a lift: today's warm-ups and jump unless the caller is showing
 * something else on purpose (the default-scheme editor shows the saved ones).
 */
export function rampFor(lift, kit, plates, { scheme = schemeFor(lift), jump = jumpFor(lift, kit.unit) } = {}) {
  const s = tidyScheme(scheme);
  const { startWeight, startReps, startRepsTo } = s;
  const steps = s.steps.map((x, position) => ({ ...x, position }));
  return buildRamp({ ...lift, startWeight, startReps, startRepsTo }, steps, kit, plates, jump);
}
